// Vue & Plugins
import { NavigationGuardReturn, RouteLocationNormalized, RouteLocationRaw, createRouter, createWebHistory } from 'vue-router'
import { routes as routesAuto, handleHotUpdate } from 'vue-router/auto-routes'

// Stores
import { useAuthUserStore } from './user/authUserStore'
import { useEventStore } from './common/eventStore'

// Models
import { ToastEvent } from './common/models/toastEvent'
import { storeToRefs } from 'pinia'

// Composables
import { useHasPermission } from './permissions/composables/useHasPermission'

import { ray } from 'node-ray/web'
import { Permission } from './sites/models/sitePermission'
import { FeatureFlagName } from './features/models/featureFlag'
import type { RouteNamedMap } from 'vue-router/auto-routes'
import { isApiError } from './axiosInstance'
import * as Sentry from '@sentry/vue'

export interface NavConfig {
    order: number
    navHeader?: boolean
    leftDrawer?: boolean
    rightDrawer?: boolean
    profileMenu?: boolean
}

export interface NavItem {
    label: string
    route: keyof RouteNamedMap
    icon?: string
    permission?: Permission
    feature?: FeatureFlagName
}

export function hasNavItem(meta: RouteLocationNormalized['meta']): meta is RouteLocationNormalized['meta'] & { navItem: NavItem } {
    return meta.navItem !== undefined
}

function toNamedRoute<T extends keyof RouteNamedMap>(
    name: T,
    params?: RouteNamedMap[T]['params']
): RouteLocationRaw {
    return { name, params } as RouteLocationRaw
}

async function beforeEach(to: RouteLocationNormalized, from: RouteLocationNormalized): Promise<NavigationGuardReturn> {
    ray().separator()
    ray().table({
        full_path: to.fullPath + ' -> ' + from.fullPath,
        path: to.path + ' -> ' + from.path,
        to_meta: to.meta,
        from_meta: from.meta,
    }).label('router').color('purple')

    const authUserStore = useAuthUserStore()
    const { isInitialising, isAuthenticated, isInitialisingSite, isSiteInitialised } = storeToRefs(authUserStore)
    const { initialiseAuth, initialiseSite } = authUserStore

    if (isInitialising.value) {
        ray('Auth is initialising, waiting for it to finish...').label('router').color('orange')
        await initialiseAuth()

        if (!isAuthenticated.value && to.meta.requiresAuth) {
            ray('Auth initialisation failed, redirecting to login').label('router').color('red')

            return toNamedRoute('/(guest)/login')
        }

        ray('Auth initialised').label('router').color('green')
    }

    if (to.meta.isGuest) {
        ray('Trying Guest route. Is logged in: ' + isAuthenticated.value).label('router').color('orange')
        return isAuthenticated.value ? toNamedRoute('/(app)/dashboard/') : undefined
    }

    if (to.meta.requiresOrganisation) {
        if (isInitialisingSite.value) {
            ray('Site is initialising, waiting for it to finish...').label('router').color('orange')
            try {
                await initialiseSite()
            } catch (error) {
                if (isApiError(error) && error.handledNavigation) {
                    return false
                } else {
                    Sentry.captureException(error)
                }
            }

            if (!isSiteInitialised.value) {
                ray('Site initialisation failed, redirecting to select site').label('router').color('red')
                return toNamedRoute('/(app)/settings/business.select')
            }

            ray('Site initialised').label('router').color('green')
        }

        const { can } = useHasPermission()
        if (to.meta.permission && !can(to.meta.permission.ability, to.meta.permission.subject)) {
            ray('Missing permission, cancelling navigation').label('router').color('orange')
            ray().separator()
            // TODO: Should maybe redirect to a 403 page here
            return false
        }
    } else {
        ray('No organisation required, skipping permission checks').label('router').color('orange')
    }

    ray('Passed before each').label('router').color('green')
    ray().separator()

    return true
}

function onError(error: unknown) {
    const eventStore = useEventStore()

    if (error instanceof Error) {
        ray().error(error).label('router').color('red')

        const chunkPattern = /Loading chunk ([\w-]+) failed/g
        const chunkMatch = error.message.match(chunkPattern)

        if (chunkMatch) {
            ray('Failed loading chunk', chunkMatch).label('router').color('red')

            const event = new ToastEvent('CliniShift has been updated. Please refresh the page to continue.')
            eventStore.addEvent(event)
        }
    } else {
        ray('Unknown router error', error).label('router').color('red')
    }
}

const router = createRouter({
    routes: routesAuto,
    history: createWebHistory(),
})

router.beforeEach((to, from) => beforeEach(to, from))
router.onError(onError)

if (import.meta.hot) {
    handleHotUpdate(router)
}

export default router